// src/services/auditMiddleware.js

// Map of API methods to audit event types
const AUDITABLE_ACTIONS = {
    // Prescription actions
    'createPrescription': {
      eventType: 'PRESCRIPTION_CREATED',
      description: 'Created new prescription'
    },
    'transmitPrescription': {
      eventType: 'PRESCRIPTION_TRANSMITTED',
      description: 'Prescription sent to pharmacy'
    },
    'cancelPrescription': {
      eventType: 'PRESCRIPTION_CANCELLED',
      description: 'Cancelled prescription'
    },
    'respondToChangeRequest': {
      eventType: 'CHANGE_REQUEST_RESPONSE',
      description: 'Responded to prescription change request'
    },
    'respondToRenewalRequest': {
      eventType: 'RENEWAL_REQUEST_RESPONSE',
      description: 'Responded to prescription renewal request'
    },
    // Access control actions
    //'updateUser': {
      //eventType: 'USER_UPDATED',
      //description: 'Updated user information'
    //},
    //'updateVelaProvider': {
      //eventType: 'PROVIDER_UPDATED',
      //description: 'Updated provider information'
    //},
    //'addVelaProvider': {
      //eventType: 'PROVIDER_ADDED',
      //description: 'Added new provider'
    //},
    // Authentication actions (to be integrated with Auth0)
    'login': {
      eventType: 'USER_LOGIN',
      description: 'User logged in'
    },
    'logout': {
      eventType: 'USER_LOGOUT',
      description: 'User logged out'
    },
    'mfaCompleted': {
      eventType: 'MFA_COMPLETED',
      description: 'Multi-factor authentication completed'
    }
  };
  
  // Middleware function to wrap API methods for auditing
  export function withAudit(apiService) {
    // Get the original request method
    const originalRequest = apiService.request;
    
    // Create a wrapper that preserves the original signature
    apiService.request = async function(endpoint, options = {}, _methodName = null) {
      try {
        // Call the original request method with all parameters
        const result = await originalRequest.call(this, endpoint, options, _methodName);
        
        // Use the method name that was passed in
        const callerName = _methodName || 'unknown';
      
        
        if (callerName && AUDITABLE_ACTIONS[callerName]) {
          const { eventType, description } = AUDITABLE_ACTIONS[callerName];
          
          // Extract relevant data from request/response
          const userId = extractUserId(options, result);
          const prescriptionId = extractPrescriptionId(options, result);
          const patientId = extractPatientId(options, result);
          const medicationId = extractMedicationId(options, result);
          const organizationId = extractOrganizationId(options, result);
          
          // Create audit log entry
          this.createAuditLogEntry({
            userId,
            organizationId,
            eventType,
            description,
            outcome: 'success',
            prescriptionId,
            patientId,
            medicationId,
            additionalData: {
              endpoint,
              method: options.method,
              responseStatus: result.status || 'OK'
            }
          }).catch(err => {
            console.error('Failed to create audit log:', err);
          });
        }
        
        return result;
      } catch (error) {
        // Log failed actions as well
        const callerName = _methodName || 'unknown';
        
        if (callerName && AUDITABLE_ACTIONS[callerName]) {
          const { eventType, description } = AUDITABLE_ACTIONS[callerName];
          
          // Extract relevant data from request
          const userId = extractUserId(options);
          const prescriptionId = extractPrescriptionId(options);
          const patientId = extractPatientId(options);
          const medicationId = extractMedicationId(options);
          const organizationId = extractOrganizationId(options);
          
          // Create audit log entry for the failure
          this.createAuditLogEntry({
            userId,
            organizationId,
            eventType,
            description: `Failed: ${description}`,
            outcome: 'failure',
            prescriptionId,
            patientId,
            medicationId,
            additionalData: {
              endpoint,
              method: options.method,
              error: error.message
            }
          }).catch(err => {
            console.error('Failed to create audit log:', err);
          });
        }
        
        throw error; // Re-throw the original error
      }
    };
    
    return apiService;
  }
  
  // Helper functions to extract relevant data from requests/responses
  function extractUserId(options, response = {}) {
    // Try to extract from body
    if (options.body) {
      try {
        const body = typeof options.body === 'string' 
          ? JSON.parse(options.body) 
          : options.body;
        
        return body.user_id || body.userId;
      } catch (e) {
        // Parsing failed, try other methods
      }
    }
    
    // Try to extract from response
    if (response.user_id) return response.user_id;
    
    // Try to extract from endpoint query params
    const match = options.endpoint?.match(/[?&]user_id=([^&]+)/);
    return match?.[1] || null;
  }

  function extractOrganizationId(options, response = {}) {
    // Try to extract from body
    if (options.body) {
      try {
        const body = typeof options.body === 'string' 
          ? JSON.parse(options.body) 
          : options.body;
        
        return body.organization_id || body.organizationId;
      } catch (e) {
        // Parsing failed, try other methods
      }
    }
    
    // Try to extract from response
    if (response.organization_id) return response.organization_id;
    
    // Try to extract from endpoint query params
    const match = options.endpoint?.match(/[?&]organization_id=([^&]+)/);
    return match?.[1] || null;
  }
  
  // Similar extraction functions for other IDs
  function extractPrescriptionId(options, response = {}) {
    // Implementation similar to extractUserId
    if (response.prescription_id) return response.prescription_id;
    if (response.prescriptionId) return response.prescriptionId;
    if(response.id) return response.id;
    
    if (options.body) {
      try {
        const body = typeof options.body === 'string' 
          ? JSON.parse(options.body) 
          : options.body;
        
        return body.prescription_id;
      } catch (e) {
        // Parsing failed
      }
    }
    
    const match = options.endpoint?.match(/\/prescription\/([^\/\?]+)/);
    return match?.[1] || null;
  }
  
  function extractPatientId(options, response = {}) {
    // Implementation similar to above
    if (response.patient_id) return response.patient_id;
    
    if (options.body) {
      try {
        const body = typeof options.body === 'string' 
          ? JSON.parse(options.body) 
          : options.body;
        
        return body.patient_id;
      } catch (e) {
        // Parsing failed
      }
    }
    
    const match = options.endpoint?.match(/\/patient\/([^\/\?]+)/);
    return match?.[1] || null;
  }
  
  function extractMedicationId(options, response = {}) {
    // Implementation similar to above
    if (response.medication_id) return response.medication_id;
    
    if (options.body) {
      try {
        const body = typeof options.body === 'string' 
          ? JSON.parse(options.body) 
          : options.body;
        
        return body.medication_id || body.drug_id;
      } catch (e) {
        // Parsing failed
      }
    }
    
    return null;
  }